import { DateTime } from 'luxon';

const LOCAL_TZ = DateTime.local().zoneName;
const UTC_TZ = 'UTC';

const getDateTimeFromObj = (dateTime, zone) => {
  const dateTimeFromSQL = DateTime.fromSQL(dateTime, { zone });
  const isValidSQLDateTime = dateTimeFromSQL?.isValid;
  if (isValidSQLDateTime) {
    return dateTimeFromSQL;
  }

  const dateTimeFromISO = DateTime.fromISO(dateTime, { zone });
  const isValidISODateTime = dateTimeFromISO?.isValid;
  if (isValidISODateTime) {
    return dateTimeFromISO;
  }

  const dateTimeFromJSDate = DateTime.fromJSDate(dateTime, { zone });
  const isValidJSDateTime = dateTimeFromJSDate?.isValid;

  return isValidJSDateTime ? dateTimeFromJSDate : null;
};

const ToFormats = {
  sql: 'toSQL',
  local: 'toLocaleString',
  iso: 'toISO',
};

const toFormat = (format, fallback) =>
  ToFormats[format] || ToFormats[fallback] || ToFormats.local;

export const getDateTime = (dateTime, opts) =>
  getDateTimeFromObj(dateTime, opts?.zone || LOCAL_TZ);

const toLocalTimeZone = (
  dateTime,
  opts = { zone: UTC_TZ, toFormat: 'local' }
) => {
  const dateTimeObj = getDateTimeFromObj(dateTime, opts?.zone || UTC_TZ);
  return dateTimeObj
    ?.toLocal()
    [toFormat(opts?.toFormat, 'local')]?.(opts?.format);
};

const toUTC = (dateTime, opts = { zone: LOCAL_TZ, toFormat: 'sql' }) => {
  const dateTimeObj = getDateTimeFromObj(dateTime, opts?.zone || LOCAL_TZ);
  return dateTimeObj?.toUTC()[toFormat(opts?.toFormat, 'sql')]?.(opts?.format);
};

export const now = () => DateTime.local();

export const fromLocalToStartOfDayTimestamp = (dateTime, timeZone) => {
  const dateTimeObj = getDateTimeFromObj(dateTime, timeZone || LOCAL_TZ);
  return dateTimeObj?.startOf('day').toISO();
};

export const fromLocalToDBStartOfDayTimestamp = (dateTime, timeZone) => {
  const dateTimeObj = getDateTimeFromObj(dateTime, timeZone || LOCAL_TZ);
  return dateTimeObj?.startOf('day').toUTC().toSQL();
};

export const fromLocalToEndOfDayTimestamp = (dateTime, timeZone) => {
  const dateTimeObj = getDateTimeFromObj(dateTime, timeZone || LOCAL_TZ);
  return dateTimeObj?.endOf('day').toISO();
};

export const fromLocalToDBEndOfDayTimestamp = (dateTime, timeZone) => {
  const dateTimeObj = getDateTimeFromObj(dateTime, timeZone || LOCAL_TZ);
  return dateTimeObj?.endOf('day').toUTC().toSQL();
};

export const fromLocalToDBTimestamp = (dateTime, timeZone) =>
  toUTC(dateTime, { zone: timeZone || LOCAL_TZ, toFormat: 'sql' });

export const fromDBToLocalTimestamp = (dateTime, format) =>
  toLocalTimeZone(dateTime, { zone: UTC_TZ, toFormat: 'local', format });

export const fromDBToLocalDateTime = dateTime => {
  const dateTimeObj = getDateTimeFromObj(dateTime, UTC_TZ);
  return dateTimeObj?.toLocal();
};

export const toLocalTimestamp = (dateTime, format) =>
  toLocalTimeZone(dateTime, { zone: LOCAL_TZ, toFormat: 'local', format });

export const toLocalDateTime = dateTime =>
  getDateTime(dateTime, { zone: LOCAL_TZ });

export const differenceDateTime = (dateTime, options) =>
  DateTime.now().diff(
    getDateTime(dateTime),
    options || ['days', 'hours', 'minutes']
  ).values;

export const calculateDateDifference = (dateTime1, dateTime2, options) => {
  if (!dateTime1 || !dateTime2) return null;
  return dateTime1.diff(
    getDateTime(dateTime2),
    options || ['days', 'hours', 'minutes']
  ).values;
};

export const getOldestDate = dates => {
  return DateTime.min(...dates.map(date => fromDBToLocalDateTime(date)));
};

export const getNewestDate = dates => {
  if (!dates?.length) return null;
  return DateTime.max(...dates.map(date => fromDBToLocalDateTime(date)));
};
